<div class="cm-wrap">
  <mat-tab-group>
    <mat-tab label="Groups">
      <div class="cm-toolbar">
        <p class="cm-note">
          Connect each <strong>group</strong> to the <strong>customer (kundekort)</strong> that gets billed for it.
          This is a <strong>global</strong> view — every group across all sites. Groups with no customer fall under
          <em>Unassigned</em> in billing &amp; reconciliation.
        </p>
        <span class="cm-spacer"></span>
        <span class="cm-chip" [class.cm-chip-warn]="unassignedGroupCount">
          Unassigned: {{ unassignedGroupCount }} / {{ (allGroups || []).length }}
        </span>
        <label class="cm-toggle">
          <input type="checkbox" [(ngModel)]="mapFilterUnassigned" (ngModelChange)="recomputeMappingGroups()"> Show only unassigned
        </label>
        <label class="cm-toggle" *ngIf="_isSuperAdmin">
          <input type="checkbox" [ngModel]="showAllCompanies" (ngModelChange)="toggleAllCompanies()"> All companies
        </label>
      </div>

      <div class="cm-toolbar">
        <mat-form-field appearance="outline" class="cm-search">
          <mat-icon matPrefix>search</mat-icon>
          <input matInput [(ngModel)]="mapSearch" (ngModelChange)="recomputeMappingGroups()" placeholder="Search group, ID, site or customer…">
          <button mat-icon-button matSuffix *ngIf="mapSearch" (click)="mapSearch = ''; recomputeMappingGroups()">
            <mat-icon>close</mat-icon>
          </button>
        </mat-form-field>
        <span class="cm-spacer"></span>
        <button mat-stroked-button color="primary" (click)="runAutoMatch()" [disabled]="!autoMatches.length"
                matTooltip="Assign only groups whose site name exactly matches one customer">
          <mat-icon>auto_fix_high</mat-icon> Auto-match exact names ({{ autoMatches.length }})
        </button>
      </div>

      <div class="cm-table-wrap">
        <table class="cm-table" *ngIf="mappingGroups.length">
          <thead>
            <tr>
              <th>Group</th>
              <th>Group ID</th>
              <th *ngIf="showAllCompanies">Company</th>
              <th>Site</th>
              <th>Customer (billed)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let g of mappingGroups" [class.cm-row-unassigned]="!g.customerId">
              <td>{{ g.name }}</td>
              <td>{{ g.groupId }}</td>
              <td *ngIf="showAllCompanies">{{ companyName(g.companyId) }}</td>
              <td class="cm-site-cell">{{ siteName(g.siteId) }}</td>
              <td class="cm-select-cell">
                <div class="cm-select-col">
                  <mat-select [value]="g.customerId || ''"
                              (selectionChange)="assignCustomer(g, $event.value)"
                              (openedChange)="mapSelectOpen[g.dbid] = $event; customerFilter = ''"
                              [disabled]="savingMap[g.dbid]"
                              placeholder="— Select customer —" class="cm-select">
                    <!-- SEED OPTION — do not remove. MatSelect._canOpen() is
                         `!panelOpen && !disabled && options.length > 0`, so a select
                         whose options are all gated behind (openedChange) can never
                         open: no options -> won't open -> openedChange never fires ->
                         options never mount. This one placeholder keeps the control
                         openable without mounting ~600 options per row. -->
                    <!-- Also keeps the closed trigger showing the current customer
                         without mounting the full list. -->
                    <mat-option *ngIf="!mapSelectOpen[g.dbid]" [value]="g.customerId || ''">{{ g.customerId ? customerName(g.customerId) : '— Select customer —' }}</mat-option>
                    <ng-container *ngIf="mapSelectOpen[g.dbid]">
                      <div class="cm-select-search">
                        <mat-icon>search</mat-icon>
                        <input [(ngModel)]="customerFilter" placeholder="Search customer…"
                               (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
                      </div>
                      <mat-option value="">— Unassigned —</mat-option>
                      <mat-option *ngFor="let c of filteredCustomers" [value]="c.kundekortId">{{ c.name }}</mat-option>
                      <div class="cm-select-empty" *ngIf="!filteredCustomers.length">No match</div>
                    </ng-container>
                  </mat-select>
                  <!-- fuzzy suggestion for unassigned rows -->
                  <button *ngIf="!g.customerId && suggestionFor(g)" type="button" class="cm-suggest"
                          (click)="applySuggestion(g)" [disabled]="savingMap[g.dbid]"
                          matTooltip="Assign this suggested customer">
                    <mat-icon>auto_fix_high</mat-icon>
                    <span>{{ suggestionFor(g).name }}</span>
                    <span class="cm-suggest-score">{{ scorePct(suggestionFor(g).score) }}%</span>
                  </button>
                </div>
                <mat-icon *ngIf="savingMap[g.dbid]" class="cm-saving">hourglass_empty</mat-icon>
              </td>
              <td class="cm-delete-cell">
                <button mat-icon-button color="warn" (click)="deleteGroup(g)" [disabled]="savingMap[g.dbid]"
                        matTooltip="Delete group">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="cm-empty" *ngIf="!mappingGroups.length">
          {{ mapSearch ? 'No groups match your search.' : (mapFilterUnassigned ? 'Every group is linked to a customer. 🎉' : 'No groups yet.') }}
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Systems">
      <div class="cm-toolbar">
        <p class="cm-note">
          Connect each physical <strong>system</strong> to the <strong>customer (kundekort)</strong> that gets billed for it.
          This matters for systems with <strong>no login</strong> — a guest-mode kiosk whose subsidy is configured directly
          on the system (e.g. Tide Buss, SAS-teknisk) has no group to link, so without a system link its subsidy silently
          falls under <em>Unassigned</em> in billing. A system can link to more than one customer only if it's genuinely
          shared between employers — otherwise stick to one.
        </p>
        <span class="cm-spacer"></span>
        <span class="cm-chip" [class.cm-chip-warn]="unlinkedSystemCount">
          Not linked: {{ unlinkedSystemCount }} / {{ (allSystems || []).length }}
        </span>
        <label class="cm-toggle">
          <input type="checkbox" [(ngModel)]="sysFilterUnlinked" (ngModelChange)="recomputeSystemRows()"> Show only not linked
        </label>
      </div>

      <div class="cm-toolbar">
        <mat-form-field appearance="outline" class="cm-search">
          <mat-icon matPrefix>search</mat-icon>
          <input matInput [(ngModel)]="sysSearch" (ngModelChange)="recomputeSystemRows()" placeholder="Search system, ID, company or customer…">
          <button mat-icon-button matSuffix *ngIf="sysSearch" (click)="sysSearch = ''; recomputeSystemRows()">
            <mat-icon>close</mat-icon>
          </button>
        </mat-form-field>
        <span class="cm-spacer"></span>
        <button mat-stroked-button color="primary" (click)="runSystemAutoMatch()" [disabled]="!systemAutoMatches.length"
                matTooltip="Link only systems whose name closely matches one customer">
          <mat-icon>auto_fix_high</mat-icon> Auto-match exact names ({{ systemAutoMatches.length }})
        </button>
      </div>

      <div class="cm-table-wrap">
        <table class="cm-table" *ngIf="systemRows.length">
          <thead>
            <tr>
              <th>System</th>
              <th>System ID</th>
              <th>Company</th>
              <th>Subsidy</th>
              <th>Linked customer(s)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let s of systemRows" [class.cm-row-unassigned]="!s.links.length">
              <td>
                {{ s.name }}
                <span class="cm-chip cm-chip-warn" *ngIf="s.isGuestSubsidy && !s.links.length" matTooltip="Guest/no-login system with a system-level subsidy and no customer link yet">
                  guest subsidy
                </span>
              </td>
              <td>{{ s.systemId }}</td>
              <td>{{ companyName(s.companyId) }}</td>
              <td>{{ s.subsidyPct ? (s.subsidyPct + '%') : '—' }}</td>
              <td class="cm-select-cell">
                <div class="cm-select-col">
                  <!-- existing links as removable chips -->
                  <span class="cm-link-chip" *ngFor="let l of s.links">
                    {{ customerName(l.kundekortId) }}
                    <button mat-icon-button (click)="removeSystemLink(l)" [disabled]="savingSys[l.dbid]" matTooltip="Remove this link">
                      <mat-icon>close</mat-icon>
                    </button>
                  </span>
                  <!-- add another link -->
                  <mat-select [value]="sysAddSelection[s.systemId] || ''"
                              (selectionChange)="addSystemLink(s, $event.value)"
                              (openedChange)="sysSelectOpen[s.systemId] = $event; sysCustomerFilter[s.systemId] = ''"
                              [disabled]="savingSys[s.systemId]"
                              placeholder="+ Link a customer" class="cm-select">
                    <!-- SEED OPTION — do not remove. MatSelect._canOpen() is
                         `!panelOpen && !disabled && options.length > 0`, so a select
                         whose options are all gated behind (openedChange) can never
                         open: no options -> won't open -> openedChange never fires ->
                         options never mount. This one placeholder keeps the control
                         openable without mounting ~600 options per row. -->
                    <mat-option *ngIf="!sysSelectOpen[s.systemId]" [value]="''">+ Link a customer</mat-option>
                    <ng-container *ngIf="sysSelectOpen[s.systemId]">
                      <div class="cm-select-search">
                        <mat-icon>search</mat-icon>
                        <input [(ngModel)]="sysCustomerFilter[s.systemId]" placeholder="Search customer…"
                               (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
                      </div>
                      <mat-option *ngFor="let c of filteredCustomersFor(s.systemId)" [value]="c.kundekortId">{{ c.name }}</mat-option>
                      <div class="cm-select-empty" *ngIf="!filteredCustomersFor(s.systemId).length">No match</div>
                    </ng-container>
                  </mat-select>
                  <!-- fuzzy suggestion for unlinked rows -->
                  <button *ngIf="!s.links.length && suggestionForSystem(s)" type="button" class="cm-suggest"
                          (click)="applySystemSuggestion(s)" [disabled]="savingSys[s.systemId]"
                          matTooltip="Link this suggested customer">
                    <mat-icon>auto_fix_high</mat-icon>
                    <span>{{ suggestionForSystem(s).name }}</span>
                    <span class="cm-suggest-score">{{ scorePct(suggestionForSystem(s).score) }}%</span>
                  </button>
                </div>
                <mat-icon *ngIf="savingSys[s.systemId]" class="cm-saving">hourglass_empty</mat-icon>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="cm-empty" *ngIf="!systemRows.length">
          {{ sysSearch ? 'No systems match your search.' : (sysFilterUnlinked ? 'Every system is linked to a customer. 🎉' : 'No systems yet.') }}
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
